import { AxiosResponse } from 'axios';
import { message } from 'antd';

import dayjs from 'dayjs';

import CustomHttpClient from '../customHttpClient';

import { formatDate, formattedPrice } from '../../utils';
import { getError } from '../../utils/getError';
import { pushIfNotEmpty } from './utils';
import userHelper from '../../utils/userHelper';

import {
  AseguradoPoliza,
  GetVoucherConfigurationServiceResponse,
  ListDocument,
  ReportDocumentEntryParams,
  ProducerContext,
  UploadFacturaResponse,
  UserInformation,
} from '../../interfaces/index';
import {
  BankAccount,
  Combo,
  CreditCardsInsured,
  DamagedPart,
  DamagedSections,
  GetCarInsuredRequest,
  GetCarInsuredResponse,
  GetCoverageDetail,
  GetDamagedPartListService,
  GetDamagedSectionsListService,
} from '../../interfaces/services';
import { CancelPolicyRequest } from '../../interfaces/services/cancel-policy-request';
import { CancellPolicyResponse } from '../../interfaces/services/cancel-policy-response';
import { GetProducerInformationResponse } from '../../interfaces/services/get-producer-information-service';
import { UploadComplementaryDocumentRequest } from '../../interfaces/services/post-complementary-documents-service';
import { DocumentClass } from '../../interfaces/enums/document-class-enum';

const getUserInfo = (selectedProducer?: ProducerContext) => {
  const {
    codigoDeEmpresa,
    codigoDeSucursal,
    intermediarioPrincipal,
    intermediarioSecundario,
  }: UserInformation = userHelper.getUser()!;

  const producerContext = selectedProducer || intermediarioSecundario;

  return {
    empresa: codigoDeEmpresa,
    sucursal: codigoDeSucursal,
    nivt: producerContext?.nivelInter,
    nivc: producerContext?.codigoInter,
    nit1: intermediarioPrincipal?.nivelInter,
    niv1: intermediarioPrincipal?.codigoInter,
  };
};

const customHttpClient = CustomHttpClient({ baseUrl: 'productores' });

const ProducersService = {
  async postUploadInvoice({
    mediatype,
    filename,
    base,
    sequence,
    dateInvoice,
    selectedProducer,
  }: ReportDocumentEntryParams): Promise<AxiosResponse<UploadFacturaResponse>> {
    const url = 'uploadFactura';

    const base64 = base.split(',')[1];

    const {
      codigoDeEmpresa,
      codigoDeSucursal,
      intermediarioSecundario,
      usuarioInterno
    }: UserInformation = userHelper.getUser()!;

    const producerContext = selectedProducer || intermediarioSecundario;
    const producerCodeAux =
      producerContext?.codMayorAux ||
      (producerContext as ProducerContext | undefined)?.codeAux;
    const producerNumberAux =
      producerContext?.nroMayorAux ||
      (producerContext as ProducerContext | undefined)?.numberAux;

    try {
      const uploadDocument = {
          "documentClass": DocumentClass.ProducerCommissionInvoice,
          "fileName": filename,
          "fileType": "application/pdf",
          "base64": base64,
          "applicationId": "WebProductores-2.0",
          "author": usuarioInterno,
          "attributes": [
            {
              "id": "DocumentTitle",
              "type": "String",
              "value": filename
            },
            {
              "id": "cmis:name",
              "type": "String",
              "value": filename
            },
            {
              "id": "CodigoEmpresa",
              "type": "String",
              "value": codigoDeEmpresa
            },
            {
              "id": "CodigoSucursal",
              "type": "String",
              "value": codigoDeSucursal
            },
            {
              "id": "CodigoMayorAuxiliar",
              "type": "String",
              "value": producerCodeAux
            },
            {
              "id": "NumeroMayorAuxiliar",
              "type": "String",
              "value": producerNumberAux
            },{
              "id": "FechaFactura",
              "type": "String",
              "value": dateInvoice.replace(/-/g, '')
            },{
              "id": "SecuenciaFactura",
              "type": "String",
              "value": sequence
            },
            
          ]
        };

      const params = { uploadDocument };

      const response = await customHttpClient.post(url, params);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postReportDocumentEntry({
    filename,
    sequence,
    dateInvoice,
    selectedProducer,
  }: ReportDocumentEntryParams): Promise<AxiosResponse<any>> {
    const url = 'informarIngresoDeDocumento';

    try {
      const { intermediarioSecundario }: UserInformation =
        userHelper.getUser()!;

      const producerContext = selectedProducer || intermediarioSecundario;
      const producerCodeAux =
        producerContext?.codMayorAux ||
        (producerContext as ProducerContext | undefined)?.codeAux;
      const producerNumberAux =
        producerContext?.nroMayorAux ||
        (producerContext as ProducerContext | undefined)?.numberAux;

      const params = {
        ...getUserInfo(selectedProducer),
        codigoMayorAuxiliar: producerCodeAux,
        numeroMayorAuxiliar: producerNumberAux,
        fechaEmision: dateInvoice,
        secuencia: sequence,
        archivo: filename,
      };

      const response = await customHttpClient.post(url, params);

      const messageSuccess: string = 'Se ha cargado la factura correctamente.';
      message.success(messageSuccess);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getInsureds(
    branch?: string,
    police?: string
  ): Promise<AxiosResponse<AseguradoPoliza>> {
    const url = 'aseguradoDePoliza';
    const params = {
      ...getUserInfo(),
      rama: branch,
      poliza: police,
    };

    try {
      const response = await customHttpClient.get(url, { params });
      const information = response.data.aseguradoPoliza;

      const data: any = {
        person: [],
        address: [],
        communication: [],
        email: [],
      };

      pushIfNotEmpty(
        data.person,
        'Fecha Nacimiento',
        formatDate(information?.fechaNacimiento)
      );
      pushIfNotEmpty(data.person, 'Nacionalidad', information?.nacionalidad);
      pushIfNotEmpty(
        data.person,
        'Documento',
        `${information?.tipoDocumento}: ${information?.nroDocumento}`
      );
      pushIfNotEmpty(data.person, 'Condición Fiscal', information?.condiFiscal);
      pushIfNotEmpty(data.person, 'CUIT', information?.cuit);

      pushIfNotEmpty(data.address, 'Domicilio', information?.domicilio);
      pushIfNotEmpty(data.address, 'Localidad', information?.localidad);
      pushIfNotEmpty(data.address, 'Código Postal', information?.codigoPostal);
      pushIfNotEmpty(data.address, 'Provincia', information?.provincia);
      pushIfNotEmpty(data.address, 'País', information?.pais);

      pushIfNotEmpty(
        data.communication,
        'Teléfono Particular',
        information?.telParticular
      );
      pushIfNotEmpty(data.communication, 'Celular', information?.telCelular);
      pushIfNotEmpty(
        data.communication,
        'Teléfono Laboral',
        information?.telLaboral
      );

      if (information?.correos.length > 0) {
        data.email.push({
          label: 'Mail',
          value: 'Soy un correo',
        });
      } else {
        data.email.push({
          label: 'Mail',
          value: 'No hay correos electrónicos cargados para el asegurado.',
        });
      }

      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getQuotes(
    branch: string,
    police: string
  ): Promise<AxiosResponse<AseguradoPoliza>> {
    const url = 'cuotasImpagasDePoliza';
    const params = {
      ...getUserInfo(),
      rama: branch,
      poliza: police,
    };

    try {
      const response = await customHttpClient.get(url, { params });
      const information = response.data.cuotas;      
      const unpaidQuotes = information.filter((item: { isPagada: string }) => item.isPagada === 'N');

      const detailQuotes = unpaidQuotes.map(
        (item: {
          suplemento: string;
          numeroDeCuota: string;
          monedaDePago: any;
          numeroDeSubCuota: any;
          fechaDeVencimiento: string;
          importeCuota: string;
        }) => {
          const columnSize = {
            xs: { span: 12 },
            sm: { span: 6 },
            md: { span: 4 },
          };

          const detailQuote: any = [];

          pushIfNotEmpty(detailQuote, 'Endoso', item?.suplemento, columnSize);

          pushIfNotEmpty(detailQuote, 'Cuota', item?.numeroDeCuota, columnSize);

          pushIfNotEmpty(
            detailQuote,
            'Subcuota',
            `${item?.monedaDePago} ${item?.numeroDeSubCuota}`,
            columnSize
          );

          pushIfNotEmpty(
            detailQuote,
            'Vencimiento',
            formatDate(item?.fechaDeVencimiento),
            columnSize
          );

          pushIfNotEmpty(
            detailQuote,
            'Importe',
            `${item?.monedaDePago} ${formattedPrice(item?.importeCuota)}`,
            columnSize
          );

          return detailQuote;
        }
      );

      return detailQuotes;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getListDocument(
    branch: string,
    articule: string
  ): Promise<ListDocument> {
    const url = 'listaCertificados';
    const params = {
      ...getUserInfo(),
      rama: branch,
      articulo: articule,
    };

    try {
      const response = await customHttpClient.get(url, { params });

      const data = response.data.documentosPdf.map(
        (item: { codigo: string; descripcion: string }) => {
          return {
            value: item.codigo,
            label: item.descripcion,
          };
        }
      );

      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getBankAccount(
    insuredCode?: string
  ): Promise<AxiosResponse<BankAccount, any>> {
    const url = 'cuentasBancariasPorAsegurado';
    const params = { ...getUserInfo(), codigoAsegurado: insuredCode };

    try {
      const response = await customHttpClient.get<BankAccount>(url, { params });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCreditCardsByInsured(
    insuredCode?: string
  ): Promise<AxiosResponse<CreditCardsInsured, any>> {
    const url = 'tarjetasDeCreditoPorAsegurado';
    const params = { ...getUserInfo(), codigoAsegurado: insuredCode };

    try {
      const response = await customHttpClient.get<CreditCardsInsured>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVoucherConfiguration(): Promise<
    AxiosResponse<GetVoucherConfigurationServiceResponse>
  > {
    const url = 'obtenerConfiguracionVoucher';
    const params = { ...getUserInfo(), fecha: dayjs().format('YYYY-MM-DD') };

    try {
      const response =
        await customHttpClient.get<GetVoucherConfigurationServiceResponse>(
          url,
          { params }
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCarInsured(requestParams: GetCarInsuredRequest): Promise<AxiosResponse<GetCarInsuredResponse>> {
    const url = 'obtenerBienesAutos';
    const params = {
      ...getUserInfo(),
      ...requestParams,
    };

    try {
      const response = await customHttpClient.get<GetCarInsuredResponse>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getDamagedSectionList(): Promise<Combo[]> {
    const url = 'obtenerSeccionesDaniadas';
    const params = {
      ...getUserInfo(),
    };
    try {
      const response = await customHttpClient.get<GetDamagedSectionsListService>(url, {
        params,
      });
      const data = response.data.seccionesDanio.map((section: DamagedSections) => {
        return {
          value: String(section.codigo),
          label: section.descripcion
        }
      })
      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getDamagedPartList(idSection: string): Promise<Combo[]> {
    const url = 'obtenerPartesDaniadas';
    const params = {
      idSeccion: idSection
    };
    try {
      const response = await customHttpClient.get<GetDamagedPartListService>(url, {
        params,
      });
      const data = response.data.partesDaniadas.map((part: DamagedPart) => {
        return {
          value: String(part.codigoParte),
          label: part.descripcion
        }
      })
      return data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  async postExecuteCancellation(
    cancellPolicyRequest: CancelPolicyRequest): Promise<AxiosResponse<any>> {
    const url = 'emision/ejecutarAnulacion';

    try {

      const response = await customHttpClient.post<CancellPolicyResponse>(url, cancellPolicyRequest);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getProducerInformation(): Promise<GetProducerInformationResponse> {
    const url = 'datosDeProductor';
    const params = {
      ...getUserInfo()
    };

    try {
      const response = await customHttpClient.get<GetProducerInformationResponse>(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postChangeProducerContactInformation(type: string, show: string, logUser: string): Promise<AxiosResponse<void>> {
    const url = 'modificarDatosContactoProductor';

    const body = {
      ...getUserInfo(),
      tipo: type,
      mostrar: show,
      usuarioLogueado: logUser
    }

    try {

      const response = await customHttpClient.post<any>(url, body);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postChangeProducerElectronicPolicy(chose: string, mail: string): Promise<AxiosResponse<void>> {
    const url = 'polizaElectronica';

    const body = {
      ...getUserInfo(),
      tipo: chose,
      mail: mail,
    }

    try {

      const response = await customHttpClient.post<any>(url, body);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getDetailCoverage(branch: string, article: string, coverage: string): Promise<AxiosResponse<GetCoverageDetail>> {
    const url = 'obtieneDetalleCobertura';
    const params = { rama: branch, articulo: article, cobertura: coverage };

    try {
      const response = await customHttpClient.get<GetCoverageDetail>(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  }, async postUploadComplementaryDocuments(complementaryDocumentResponse : UploadComplementaryDocumentRequest): Promise<AxiosResponse<void>> {
    const url = 'uploadDocumentosComplementarios';
    try {

      const response = await customHttpClient.post<any>(url, complementaryDocumentResponse);

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  }

};

export default ProducersService;